<template>
	<div
		class="wrapper wrapper--body"
		:view-state="viewStateString"
		:exhibition-id="currentExhibitionID"
		:article-id="currentArticleID"
		:article-modifier="articleModifier"
		:tour-id="tourId"
		dev-mode="false"
		:style="
			pageTransitionSwipe
				? 'width: 100%; height: 100%; position:fixed; left:0; top:0;'
				: 'width: 100%; position:relative;'
		"
	>
		<UiNavigation position="top" :hideOnScroll="true" :aria-hidden="viewStateAriaHideMainElements" />
		<UiNavigation position="right" />
		<UiNavigation position="bottom" :hideOnScroll="true" :aria-hidden="viewStateAriaHideMainElements" />
		<keep-alive>
			<transition name="ui--left">
				<UiNavigation
					ref="uiNavigationLeft"
					v-if="viewIsOpen(uiComponents.navigationLeft)"
					position="left"
					closeButton
					:style="
						viewIsOpen('ui--left') && swipeDelta.x != 0
							? { transform: `translate3d(${swipeDelta.x}px, 0, 0)` }
							: null
					"
				>
					<Swipe ref="menuSwipe" swipeRight v-model="swipeDelta" @swipe="onMenuSwipe($event)" />
				</UiNavigation>
			</transition>
		</keep-alive>
		<transition name="ui--keypad">
			<UiKeypad v-if="viewIsOpen(uiComponents.uiKeypad)" />
		</transition>
		<transition name="ui--ar-warning">
			<UiARWarning v-if="viewIsOpen(uiComponents.uiARWarning)" />
		</transition>
		<transition name="ui--ar-corekit-warning">
			<UiARCoreKitWarning v-if="viewIsOpen(uiComponents.uiARCoreKitWarning)" />
		</transition>
		<transition name="ui--language">
			<UiLanguage v-if="viewIsOpen(uiComponents.uiLanguage)" />
		</transition>
		<transition name="ui--fontSize">
			<UiFontSize v-if="viewIsOpen(uiComponents.uiFontSize)" />
		</transition>
		<transition name="ui--colorMode">
			<UiColorMode v-if="viewIsOpen(uiComponents.uiColorMode)" />
		</transition>

		<UiNavigation position="tour" :hideOnScroll="false" :aria-hidden="viewStateAriaHideMainElements" />
		<UiMessages />
		<SystemInfo />
		<SystemBeacons />
		<SystemBeaconList />
		<SystemLock />
		<SystemSecurity />
		<UiLoader />

		<UiAudio />
		<UiContent />
		<UiVideo />

		<transition name="main-content">
			<keep-alive>
				<PageTransitionSwipe
					v-if="
						!mainContentObscured &&
						pageTransitionSwipe &&
						!!dataSourceCFG &&
						viewIsOpen(uiComponents.wrapperArticleCurrent)
					"
					:aria-hidden="viewStateAriaHideMainElements"
				/>
				<PageTransitionSimple v-if="!mainContentObscured && !pageTransitionSwipe && !!dataSourceCFG" />
			</keep-alive>
		</transition>
		<!-- Main content -->

		<transition name="ui--map">
			<keep-alive>
				<UiMap
					style="display: flex !important"
					v-if="viewIsOpen(uiComponents.uiMap)"
					:aria-hidden="viewStateAriaHideMainElements"
				>
					<!-- Locations search -->
					<transition name="search--locations">
						<!-- avoid <keep-alive> here as that causes unwanted updates to search results -->
						<UiSearch
							scope="locations"
							style="position: relative !important"
							v-if="viewIsOpen(uiComponents.uiSearchLocations)"
						/>
					</transition>
				</UiMap>
			</keep-alive>
		</transition>

		<!-- areabeacons search -->
		<transition name="search--areabeacons">
			<!-- avoid <keep-alive> here as that causes unwanted updates to search results -->
			<UiSearch scope="areabeacons" v-if="viewIsOpen(uiComponents.uiSearchAreabeacons)" />
		</transition>

		<slot></slot>
	</div>
</template>

<style>
.element--list {
	touch-action: pan-y;
}
</style>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';

import { mutationTypes, getterTypes } from '@/types';
import { uiComponents, uiStates, viewIsOpen } from '@/tools/viewState';

import SystemInfo from '@/components/system/info.vue';
import SystemBeacons from '@/components/system/beacons.vue';
import SystemBeaconList from '@/components/system/beacon-list.vue';
import SystemLock from '@/components/system/lock.vue';
import SystemSecurity from '@/components/system/security.vue';
import UiLoader from '@/components/ui/loader.vue';
import UiMessages from '@/components/ui/messages.vue';
import UiVideo from '@/components/ui/video.vue';
import UiNavigation from '@/components/ui/navigation.vue';
import UiContent from '@/components/ui/content.vue';
import PageTransitionSwipe from '@/components/pageTransition/swipe.vue';
import PageTransitionSimple from '@/components/pageTransition/simple.vue';

// Dynamic loaded components
const UiAudio = () => import(/* webpackChunkName: "components/ui/audio.vue" */ '@/components/ui/audio.vue');
const Swipe = () => import(/* webpackChunkName: "components/ui/swipe.vue" */ '@/components/ui/swipe.vue');
const UiSearch = () =>
	import(/* webpackChunkName: "components/ui/search.vue" */ '@/components/ui/search.vue');
const UiMap = () => import(/* webpackChunkName: "components/ui/map.vue" */ '@/components/ui/map.vue');
const UiLanguage = () =>
	import(/* webpackChunkName: "components/ui/language.vue" */ '@/components/ui/language.vue');
const UiFontSize = () =>
	import(/* webpackChunkName: "components/ui/fontsize.vue" */ '@/components/ui/fontsize.vue');
const UiKeypad = () =>
	import(/* webpackChunkName: "components/ui/keypad.vue" */ '@/components/ui/keypad.vue');
const UiColorMode = () =>
	import(/* webpackChunkName: "components/ui/colormode.vue" */ '@/components/ui/colormode.vue');
const UiARWarning = () =>
	import(/* webpackChunkName: "components/ui/arWarning.vue" */ '@/components/ui/arWarning.vue');
const UiARCoreKitWarning = () =>
	import(/* webpackChunkName: "components/ui/arCoreKitWarning.vue" */ '@/components/ui/arCoreKitWarning.vue');

export default {
	components: {
		UiARWarning,
		UiARCoreKitWarning,
		UiNavigation,
		SystemInfo,
		SystemBeacons,
		SystemBeaconList,
		SystemLock,
		SystemSecurity,
		UiKeypad,
		UiLoader,
		UiLanguage,
		UiFontSize,
		UiColorMode,
		UiMessages,
		UiAudio,
		UiVideo,
		UiContent,
		UiSearch,
		UiMap,
		Swipe,
		PageTransitionSwipe,
		PageTransitionSimple,
	},
	data() {
		return { swipeDelta: { x: 0, y: 0 } };
	},
	computed: {
		...mapState(['dataSourceCFG', 'currentExhibitionID', 'currentArticleID', 'viewState', 'search']),
		...mapGetters([
			'viewStateString',
			'mainContentObscured',
			'viewStateAriaHideMainElements',
			'getArticleContentById',
			getterTypes.pageTransitionSwipe,
		]),
		editorApiActive() {
			return this.$store.state.editorApi.active;
		},
		articleModifier() {
			const data = this.getArticleContentById(this.$store.state.currentArticleID);
			const articleContent = this.editorApiActive ? this.$store.state.editorApi.content : data.content;
			return articleContent ? articleContent.modifier : 'standard';
		},
		tourId() {
			return this.$store.state.tour.current;
		},
	},
	methods: {
		...mapMutations([mutationTypes.viewStateSetUiState]),
		// closeListener(id) {},
		viewIsOpen(name) {
			return viewIsOpen(this.$store.state, name);
		},
		onMenuSwipe({ direction }) {
			const $swipe = this.$refs.menuSwipe;

			// 0 means open
			if (direction === 'right') $swipe.$parent.close();
			else $swipe.$parent.maximise();

			$swipe.reset();
		},
	},
	beforeCreate() {
		this.uiComponents = uiComponents;
		this.uiStates = uiStates;
	},
};
</script>
